<template>
  <div class="w-full pb-2 flex flex-col justify-start items-start gap-[15px]">
    <div class="border-b-2 border-solid border-[#2463eb] pb-[8px]">
      <h2 class="text-[#2463eb] text-[14px] leading-[20px] font-semibold">
        Sales Reports
      </h2>
    </div>
    <div
      class="border-[1.5px] border-solid border-[#DBDCE0] rounded-[4px] w-full"
    >
      <div class="bg-[#FBFBFB] w-full px-[24px] py-[13px]">
        <p class="text-[#353535] text-[12px] leading-[18px] font-medium">
          List Of All Reports
        </p>
      </div>
      <div class="w-full bg-white flex flex-col justify-start">
        <Dropdown
          v-for="(dropdown, index) in dropdowns"
          :key="index"
          :title="dropdown.title"
          :description="dropdown.description"
          :items="dropdown.items"
          :open="dropdownState[dropdown.key]"
          @toggle="toggleDropdown(dropdown.key)"
          @viewItem="handleAction"
        />
      </div>
    </div>
    <component
      v-for="(modal, index) in visibleModals"
      :key="index"
      :is="modal.component"
      :close="() => toggleModal(modal.name)"
    />
  </div>
</template>

<script>
import SystemPermissions from "@/permissions";
import Dropdown from "../../components/Reports/Dropdown.vue";

import SalesByProduct from "../../components/Reports/SalesByProduct.vue";
import salesByDistributor from "../../components/Reports/salesByDistributorBySKU.vue";
import PurchaseProcessLeadTimeSalesManagerReport from "../../components/Reports/PurchaseProcessLeadTimeSalesManagerReport.vue";
import PurchaseProcessLeadTimeReport from "@/components/Reports/PurchaseProcessLeadTimeReport.vue";
import OpenPurchase from "../../components/Reports/OpenPurchase.vue";
import OpenPurchasesSummary from "../../components/Reports/OpenPurchasesSummary.vue";
import TotalOrderByDistributor from "../../components/Reports/TotalOrderByDistributor.vue";
import TotalOrderByStatus from "../../components/Reports/TotalOrderByStatus.vue";
import ProductPerformance from "../../components/Reports/ProductPerformance.vue";
import ProductPerformanceDistributor from "../../components/Reports/ProductPerformanceDistributor.vue";
import orderDeliveryReport from "../../components/Reports/OrderDeliveryReport.vue";
import queuedReports from "../../components/Reports/QueuedReports.vue";

export default {
  mixins: [SystemPermissions.Mixin],
  name: "reports",
  components: {
    Dropdown,
    SalesByProduct,
    salesByDistributor,
    PurchaseProcessLeadTimeSalesManagerReport,
    PurchaseProcessLeadTimeReport,
    OpenPurchase,
    OpenPurchasesSummary,
    TotalOrderByDistributor,
    TotalOrderByStatus,
    ProductPerformance,
    ProductPerformanceDistributor,
    orderDeliveryReport,
    queuedReports,
  },
  data() {
    return {
      dropdownState: {
        salesOpen: false,
        ordersOpen: false,
        productsOpen: false,
        queuedReportsOpen: false,
      },
      modals: {
        salesProduct: false,
        salesDistributor: false,
        processTime: false,
        timeSalesManager: false,
        openPurchases: false,
        openPurchasesSummary: false,
        totalOrdersDistributor: false,
        totalOrdersStatus: false,
        productPerformance: false,
        productPerformanceDistributor: false,
        orderDeliveryReport: false,
        queuedReports: false,
      },
      dropdowns: [
        {
          title: "Sales",
          description: "View detailed sales reports by SKUs and distributors.",
          key: "salesOpen",
          items: [
            { label: "Sales By SKUs", action: "salesProduct" },
            {
              label: "Sales By Distributor By SKUs",
              action: "salesDistributor",
            },
            {
              label: "Avg Process Lead Time (Order Stages)",
              action: "processTime",
            },
            {
              label: "Avg Process Lead Time (Sales Mgr)",
              action: "timeSalesManager",
            },
          ],
        },
        {
          title: "Orders",
          description:
            "Manage and view order details by distributor, state, and status",
          key: "ordersOpen",
          items: [
            { label: "Open Purchases", action: "openPurchases" },
            { label: "Open Purchases Summary", action: "openPurchasesSummary" },
            {
              label: "Total Orders By Distributor",
              action: "totalOrdersDistributor",
            },
            { label: "Total Orders By Status", action: "totalOrdersStatus" },
            {
              label: "Order Delivery Report",
              action: "orderDeliveryReport",
            },
          ],
        },
        {
          title: "Products",
          description: "Manage product performance by distributor and state",
          key: "productsOpen",
          items: [
            { label: "Product Performance", action: "productPerformance" },
            {
              label: "Product Performance (Distributor)",
              action: "productPerformanceDistributor",
            },
          ],
        },
        {
          title: "Queued Reports",
          description: "View all queued reports",
          key: "queuedReportsOpen",
          items: [{ label: "Queued Reports", action: "queuedReports" }],
        },
      ],
      modalsConfig: {
        salesProduct: { component: "SalesByProduct" },
        salesDistributor: { component: "salesByDistributor" },
        processTime: { component: "PurchaseProcessLeadTimeReport" },
        timeSalesManager: {
          component: "PurchaseProcessLeadTimeSalesManagerReport",
        },
        openPurchases: { component: "OpenPurchase" },
        openPurchasesSummary: { component: "OpenPurchasesSummary" },
        totalOrdersDistributor: { component: "TotalOrderByDistributor" },
        totalOrdersStatus: { component: "TotalOrderByStatus" },
        productPerformance: { component: "ProductPerformance" },
        productPerformanceDistributor: {
          component: "ProductPerformanceDistributor",
        },
        orderDeliveryReport: { component: "orderDeliveryReport" },
        queuedReports: { component: "queuedReports" },
      },
    };
  },
  computed: {
    visibleModals() {
      return Object.entries(this.modals)
        .filter(([_, isVisible]) => isVisible)
        .map(([name]) => ({
          name,
          component: this.modalsConfig[name].component,
        }));
    },
  },
  methods: {
    toggleDropdown(key) {
      Object.keys(this.dropdownState).forEach((dropdownKey) => {
        if (dropdownKey !== key) {
          this.dropdownState[dropdownKey] = false;
        }
      });
      this.dropdownState[key] = !this.dropdownState[key];
    },
    toggleModal(modal) {
      this.modals[modal] = !this.modals[modal];
    },
    handleAction(item) {
      if (item && item.action) {
        this.toggleModal(item.action);
      } else {
        console.error("Item is missing an action:", item);
      }
    },
  },
};
</script>
